import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import Navigation from './Components/Navigation';

// Job component imports
import JobRequirements from '../JobComponents/JobRequirements';
import JobCandidates from '../JobComponents/JobCandidates';
import JobEvaluations from '../JobComponents/JobEvaluations';
import AddCandidatePopup from '../JobComponents/AddCandidatePopup';
import JobStatus from '../JobComponents/Utils/JobStatus';

/**
 * JobDetails Component
 * Displays detailed information about a job including requirements, questionnaire,
 * practical quiz, evaluations, and candidates.
 */
const JobDetails = () => {
  // ===== HOOKS & CONTEXT =====
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useSession();
  const contentRef = useRef(null);

  // ===== STATE =====
  // Job data state
  const [jobData, setJobData] = useState({
    loading: true,
    error: null,
    details: null
  });
  
  // Candidates data state (loaded independently)
  const [candidatesData, setCandidatesData] = useState({
    loading: false,
    error: null,
    data: []
  });
  
  // UI state
  const [activeTab, setActiveTab] = useState('requirements');
  const [isAddCandidateOpen, setIsAddCandidateOpen] = useState(false);

  // ===== UTILITY FUNCTIONS =====
  
  /**
   * Handles API errors, especially 401 unauthorized errors
   * @param {Error} err - The error object
   * @returns {boolean} - Whether the error was handled
   */
  const handleApiError = (err) => {
    console.error('API Error:', err);
    console.log('Error status code:', err.status);
    
    // If it's a 401 error, it's an expired session - log out and redirect to login
    if (err.status === 401) {
      console.log('Session expired or unauthorized. Redirecting to login page.');
      logout();
      navigate('/login');
      return true; // Error was handled
    }
    
    return false; // Error needs further handling
  };
  
  /**
   * Formats a date string to a more readable format
   * @param {string} dateString - The date string to format
   * @returns {string} - Formatted date string
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  /**
   * Extracts job ID from URL query parameters
   * @returns {string} - The job ID
   */
  const getJobId = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('jid');
  };

  /**
   * Parses a JSON field safely
   * @param {string|Object} field - The field to parse
   * @returns {Object|null} - Parsed object or null
   */
  const parseJsonField = (field) => {
    if (!field) return null;
    try {
      return typeof field === 'string' ? JSON.parse(field) : field;
    } catch (err) {
      console.error('Error parsing JSON field:', err);
      return null;
    }
  };
  
  /**
   * Transforms API response data into a clean object
   * @param {Array} responseData - The API response data
   * @returns {Object|null} - Transformed job data or null
   */
  const transformJobData = (responseData) => {
    if (!Array.isArray(responseData) || responseData.length === 0) {
      return null;
    }
    
    const data = responseData[0];
    return {
      id: data.job_id,
      name: data.job_name,
      companyName: data.company_name,
      status: data.status,
      createdTime: data.created_at,
      signedURL: data.signedURL,
      requirements: parseJsonField(data.job_requirements_json),
      questionnaire: parseJsonField(data.questionnaireJSON),
      practicalQuiz: parseJsonField(data.practicalQuizJSON),
      candidates: Array.isArray(data.candidates) ? data.candidates : [],
      job_category_json: data.job_category_json
    };
  };

  // ===== DATA FETCHING =====
  
  /**
   * Fetches job data from the API
   * @param {string} jobId - The job ID to fetch
   * @returns {Promise} - Promise resolving to job data
   */
  const fetchJobData = async (jobId) => {
    if (!jobId) {
      setJobData({
        loading: false,
        error: 'Job ID is missing',
        details: null
      });
      return;
    }

    try {
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      const url = new URL(API_ENDPOINTS.GET_JOB);
      url.searchParams.append('jid', jobId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status; // Add status to the error object for use in catch block
        throw error;
      }
      
      const responseData = await response.json();
      console.log('API Response from get-a-job:', responseData);
      
      const transformedData = transformJobData(responseData);
      
      setJobData({
        loading: false,
        error: null,
        details: transformedData
      });
      
      // Note: In the future, candidates will be loaded separately
      
      return responseData;
    } catch (err) {
      // Use the helper function to handle common API errors
      if (!handleApiError(err)) {
        // If the error wasn't handled by the helper, update the state with the error
        setJobData({
          loading: false,
          error: err.message || 'Failed to fetch job details',
          details: null
        });
      }
    }
  };

  /**
   * Fetches candidates for a job from the API
   * @param {string} jobId - The job ID to fetch candidates for
   */
  const fetchCandidates = async (jobId) => {
    if (!jobId) return;
    
    // Set loading state
    setCandidatesData(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Make GET request to the candidates endpoint with job ID as a parameter
      const url = new URL(API_ENDPOINTS.GET_JOB_CANDIDATES);
      url.searchParams.append('jid', jobId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status; // Add status to the error object for use in catch block
        throw error;
      }
      
      // Try to parse the response as JSON, use empty array if it fails
      let candidatesData = [];
      try {
        candidatesData = await response.json();
        console.log('Candidates data:', candidatesData);
      } catch (parseError) {
        console.log('Empty or invalid response received for candidates');
      }
      
      // Update candidates state with empty array if parsing failed
      setCandidatesData({
        loading: false,
        error: null,
        data: Array.isArray(candidatesData) ? candidatesData : []
      });
    } catch (err) {
      // Use the helper function to handle common API errors
      if (!handleApiError(err)) {
        // If the error wasn't handled by the helper, update the state with the error
        setCandidatesData(prev => ({
          ...prev,
          loading: false,
          error: err.message || 'Failed to fetch candidates'
        }));
      }
    }
  };

  // ===== EVENT HANDLERS =====
  
  /**
   * Handles tab change
   * @param {string} tabName - The tab to switch to
   */
  const handleTabChange = (tabName) => {
    // First update the active tab
    setActiveTab(tabName);
    
    // Update URL with hash fragment
    navigate(`${location.pathname}${location.search}#${tabName}`, { replace: true });
    
    // If switching to candidates tab, fetch candidates
    if (tabName === 'candidates') {
      fetchCandidates(getJobId());
    }
    
    // Then scroll the window to the top
    window.scrollTo(0, 0);
    
    // For good measure, also scroll the content container to the top
    // This is done with a small delay to ensure the DOM has updated
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }, 10);
  };


  /**
   * Handles view candidate button click
   * @param {string} candidateId - The candidate ID to view
   */
  const handleViewCandidate = (candidateId) => {
    navigate(`/candidate-details?cid=${candidateId}`);
  };
  
  /**
   * Handles add candidate button click
   */
  const handleAddCandidate = () => {
    setIsAddCandidateOpen(true);
  };
  
  /**
   * Handles candidate form submission
   * @param {Object} candidateData - The candidate data to submit
   * @returns {Promise} - Promise resolving to success or error
   */
  const handleCandidateSubmit = async (candidateData) => {
    try {
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Prepare the request payload with only the specific fields we need
      const payload = {
        fname: candidateData.firstName,
        lname: candidateData.lastName,
        job_id: getJobId(),
        email: candidateData.email,
        evaluation_id: candidateData.evaluationId,
        send_invite_email: candidateData.sendInviteEmail
      };
      
      console.log('Submitting candidate with payload:', payload);
      
      const response = await fetch(API_ENDPOINTS.ADD_CANDIDATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status; // Add status to the error object for use in catch block
        throw error;
      }
      
      // Refresh the candidates list after adding a new candidate
      await fetchCandidates(getJobId());
      setIsAddCandidateOpen(false);
      return true;
    } catch (err) {
      // Use the helper function to handle common API errors
      if (handleApiError(err)) {
        return; // Error was handled by the helper
      }
      
      // If the error wasn't handled by the helper, reject the promise
      return Promise.reject(err);
    }
  };

  // ===== EFFECTS =====
  
  // Fetch job data when job ID changes
  useEffect(() => {
    const jobId = getJobId();
    fetchJobData(jobId);
  }, [location.search]);
  
  // Check for hash fragment in URL and set active tab accordingly
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['requirements', 'evaluations', 'candidates'].includes(hash)) {
      setActiveTab(hash);
      
      // If switching to candidates tab, fetch candidates
      if (hash === 'candidates') {
        fetchCandidates(getJobId());
      }
    }
  }, [location.hash]);
  
  // Update document title when job data changes
  useEffect(() => {
    if (jobData.details?.name) {
      document.title = jobData.details.name;
    } else {
      document.title = "Job Details";
    }
    
    // Reset title when component unmounts
    return () => {
      document.title = "VaaS Admin";
    };
  }, [jobData.details]);

  // ===== RENDER HELPERS =====
  
  /**
   * Renders the tab content based on active tab
   * @returns {JSX.Element} - The tab content
   */
  const renderTabContent = () => {
    switch (activeTab) {
      case 'requirements':
        return jobData.details?.requirements ? (
          <JobRequirements 
            jobRequirement={jobData.details.requirements.jobRequirement || jobData.details.requirements} 
            jdPDF={jobData.details.signedURL ? 
              { url: jobData.details.signedURL, filename: "Job Description" } : null}
          />
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-600">No job requirements data available.</p>
          </div>
        );
        
      case 'evaluations':
        return <JobEvaluations jobData={jobData.details} />;
        
      case 'candidates':
        return (
          <JobCandidates 
            candidates={candidatesData.data} 
            onViewCandidate={handleViewCandidate} 
            onAddCandidate={handleAddCandidate}
            loading={candidatesData.loading}
            error={candidatesData.error}
            onCandidateDeleted={() => fetchCandidates(getJobId())}
          />
        );
        
      default:
        return null;
    }
  };

  /**
   * Renders a tab button
   * @param {string} tabName - The tab name
   * @param {string} label - The tab label
   * @returns {JSX.Element} - The tab button
   */
  const renderTabButton = (tabName, label) => (
    <button
      onClick={() => handleTabChange(tabName)}
      className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
        activeTab === tabName
          ? 'border-blue-500 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {label}
    </button>
  );

  // ===== RENDER =====
  return (
    <div className="min-h-screen bg-gray-100 p-5 pt-[76px]">
      <Navigation 
        title={
          jobData.loading ? (
            <span className="inline-flex items-center">
              <span className="animate-pulse">Loading job details...</span>
            </span>
          ) : jobData.details ? (
            `${jobData.details.name} at ${jobData.details.companyName}`
          ) : (
            "Job Details"
          )
        } 
      />
      
      {/* Main Content */}
      <div className="bg-white rounded-lg shadow">
        {/* Loading State */}
        {jobData.loading ? (
          <div className="text-center py-8">
            <p className="text-gray-600">Loading job details...</p>
          </div>
        ) 
        /* Error State */
        : jobData.error ? (
          <div className="text-center py-8">
            <p className="text-red-500">{jobData.error}</p>
            <p className="text-gray-600 mt-2">Please try again or go back to the jobs page.</p>
          </div>
        ) 
        /* Data Loaded State */
        : jobData.details ? (
          <div>
            {/* Job title and tabs - sticky header below the main menu */}
            <div className="sticky top-[76px] z-30 bg-white shadow-sm">
              {/* Tabs Navigation */}
              <div className="border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <nav className="flex -mb-px">
                    {renderTabButton('requirements', 'Requirements')}
                    {renderTabButton('evaluations', 'Evaluation Templates')}
                    {renderTabButton('candidates', 'Candidates')}
                  </nav>
                  <div className="flex flex-wrap gap-4 items-center pr-4">
                    <div className="flex flex-wrap items-center">
                      {getJobId() && (
                        <span className="text-xs text-gray-500">
                          Job ID: {getJobId()}
                        </span>
                      )}
                      {getJobId() && jobData.details.createdTime && (
                        <span className="text-xs text-gray-500 mx-2">|</span>
                      )}
                      {jobData.details.createdTime && (
                        <span className="text-xs text-gray-500">
                          Created: {formatDate(jobData.details.createdTime)}
                        </span>
                      )}
                    </div>
                    {jobData.details.status && (
                      <div className="ml-auto">
                        <JobStatus status={jobData.details.status} showDot={true} size="md" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Tab Content - Using key to force remount when tab changes */}
            <div className="p-6 overflow-auto" ref={contentRef} key={activeTab}>
              {renderTabContent()}
            </div>
          </div>
        ) 
        /* No Data State */
        : (
          <div className="text-center py-8">
            <p className="text-gray-600">No job data available.</p>
          </div>
        )}
      </div>
      
      {/* Add Candidate Popup */}
      <AddCandidatePopup
        isOpen={isAddCandidateOpen}
        onClose={() => setIsAddCandidateOpen(false)}
        onSuccess={() => fetchCandidates(getJobId())}
        jobId={getJobId()}
      />
    </div>
  );
};

export default JobDetails;
